import EntityMemberCommand from "_shared/entity-members/_api/commands/entity-member-api-command";
import FeeScheduleCategoryCommand, {
  FeeScheduleCategoryCommandPayload,
} from "marketplace/teams/fee-schedules/_api/commands/fee-schedule-category-command";
import Session from "users/__session/session";
import { Masked, MaskedTeam } from "work/_shared/masked-fields";
import ReplaceableDocumentAPIRequest, {
  ReplaceableDocumentAPIRequestPayload,
} from "work/proposals/_api/commands/replaceable-document-api-request";
import WorkTeamAPICommand, { WorkTeamAPIRequestPayload } from "work/proposals/_api/commands/work-team-api-command";
import Proposal from "work/proposals/proposal";
import ProposalReviewerCommand, {
  ProposalReviewerAPIRequestPayload,
} from "work/proposals/reviewers/_api/command-contracts/proposal-reviewer-command";

export type WorkProposalAPIRequestPayload = {
  RFPId?: string;
  creatorType?: string;
  creatorEntityId?: string;
  name?: string;
  description?: string;
  clientTeam?: WorkTeamAPIRequestPayload;
  vendorTeam?: WorkTeamAPIRequestPayload;
  feeSchedule?: FeeScheduleCategoryCommandPayload[];
  startDate?: string;
  endDate?: string;
  discount?: number;
  negotiable?: boolean;
  responseDueBy?: string;
  supersedesId?: string;
  clientReviewers?: ProposalReviewerAPIRequestPayload[];
  vendorReviewers?: ProposalReviewerAPIRequestPayload[];
  clientPolicies?: ReplaceableDocumentAPIRequestPayload[];
  vendorPolicies?: ReplaceableDocumentAPIRequestPayload[];
  conflictsCheckWaived?: boolean | null;
  teamRestricted?: boolean | null;
  conflicts?: ReplaceableDocumentAPIRequestPayload[];
  clientTeamTemplateIds?: string[];
  vendorTeamTemplateIds?: string[];
  clientFeeScheduleTemplateIds?: string[];
  vendorFeeScheduleTemplateIds?: string[];
  redlining?: object;
};

export default class WorkProposalAPIRequest {
  RFPId?: string;
  creatorType?: string;
  creatorEntityId?: string;
  name?: string;
  description?: string;
  clientTeam?: WorkTeamAPICommand;
  vendorTeam?: WorkTeamAPICommand;
  feeSchedule?: FeeScheduleCategoryCommand[];
  startDate?: string;
  endDate?: string;
  discount?: number;
  negotiable?: boolean;
  responseDueBy?: string;
  supersedesId?: string;
  clientReviewers?: ProposalReviewerCommand[];
  vendorReviewers?: ProposalReviewerCommand[];
  clientPolicies?: ReplaceableDocumentAPIRequest[];
  vendorPolicies?: ReplaceableDocumentAPIRequest[];
  conflictsCheckWaived?: boolean | null;
  teamRestricted?: boolean | null;
  conflicts?: ReplaceableDocumentAPIRequest[];
  clientTeamTemplateIds?: string[];
  vendorTeamTemplateIds?: string[];
  clientFeeScheduleTemplateIds?: string[];
  vendorFeeScheduleTemplateIds?: string[];

  constructor(proposal: Proposal, session: Readonly<Session>) {
    this.RFPId = proposal.RFPId?.value;
    this.name = proposal.name?.value;
    this.description = proposal.description?.value;
    this.vendorTeam =
      Masked.isPresentNotMasked(proposal.vendorTeam) && proposal.vendorTeam.leader
        ? Object.assign(new WorkTeamAPICommand(), {
            leader: proposal.vendorTeam.leader?.legalEntityId
              ? new EntityMemberCommand(proposal.vendorTeam.leader)
              : undefined,
            members: proposal.vendorTeam.members.map((member) => new EntityMemberCommand(member)),
            marketplaceId: proposal.vendorTeam.marketplaceId?.value,
          })
        : undefined;
    this.feeSchedule = Masked.isNotMasked(proposal.feeSchedule)
      ? proposal.feeSchedule.map((category) => {
          return new FeeScheduleCategoryCommand(category);
        })
      : undefined;
    this.discount = Masked.isPresentNotMasked(proposal.discount) ? proposal.discount.value : undefined;
    this.creatorType = session.accountType;
    this.creatorEntityId = session.user!.legalEntityId.value;
    this.startDate = proposal.startDate?.toISOString();
    this.endDate = proposal.endDate?.toISOString();
    this.responseDueBy = proposal.responseDueBy?.toISOString();
    this.negotiable = proposal.negotiable.value;
    this.clientReviewers = proposal.clientReviewers.map((reviewer) => {
      return Object.assign(new ProposalReviewerCommand(reviewer));
    });
    this.vendorReviewers = proposal.vendorReviewers.map((reviewer) => {
      return Object.assign(new ProposalReviewerCommand(reviewer));
    });
    this.supersedesId = proposal.supersedes?.id?.value;
    this.clientTeam = Object.assign(new WorkTeamAPICommand(), {
      leader: MaskedTeam.isPresentNotMasked(proposal.clientLeader)
        ? new EntityMemberCommand(proposal.clientLeader)
        : undefined,
    });
    this.clientPolicies = Masked.isNotMasked(proposal.clientPolicies)
      ? proposal.clientPolicies.map((document) => {
          return Object.assign(new ReplaceableDocumentAPIRequest(document));
        })
      : undefined;
    this.vendorPolicies = Masked.isNotMasked(proposal.vendorPolicies)
      ? proposal.vendorPolicies.map((document) => {
          return Object.assign(new ReplaceableDocumentAPIRequest(document));
        })
      : undefined;

    this.conflictsCheckWaived = Masked.isPresentNotMasked(proposal.conflictsCheckWaived)
      ? proposal.conflictsCheckWaived.value
      : undefined;

    this.teamRestricted = Masked.isPresentNotMasked(proposal.teamRestricted)
      ? proposal.teamRestricted.value
      : undefined;

    this.conflicts = Masked.isPresentNotMasked(proposal.conflicts)
      ? proposal.conflicts.map((document) => {
          return Object.assign(new ReplaceableDocumentAPIRequest(document));
        })
      : undefined;

    this.clientTeamTemplateIds = proposal.clientTeamTemplateIds.map((id) => id.value);
    this.vendorTeamTemplateIds = proposal.vendorTeamTemplateIds.map((id) => id.value);
    this.clientFeeScheduleTemplateIds = proposal.clientFeeScheduleTemplateIds.map((id) => id.value);
    this.vendorFeeScheduleTemplateIds = proposal.vendorFeeScheduleTemplateIds.map((id) => id.value);
  }

  public get payload(): WorkProposalAPIRequestPayload {
    return {
      RFPId: this.RFPId,
      creatorType: this.creatorType,
      creatorEntityId: this.creatorEntityId,
      name: this.name,
      description: this.description,
      clientTeam: this.clientTeam?.payload,
      teamRestricted: this.teamRestricted,
      vendorTeam: this.vendorTeam?.payload,
      feeSchedule: this.feeSchedule?.map((category) => category.payload),
      startDate: this.startDate,
      endDate: this.endDate,
      discount: this.discount,
      negotiable: this.negotiable,
      responseDueBy: this.responseDueBy,
      supersedesId: this.supersedesId,
      clientReviewers: this.clientReviewers?.map((reviewer) => reviewer.payload),
      vendorReviewers: this.vendorReviewers?.map((reviewer) => reviewer.payload),
      clientFeeScheduleTemplateIds: this.clientFeeScheduleTemplateIds,
      vendorFeeScheduleTemplateIds: this.vendorFeeScheduleTemplateIds,
      clientTeamTemplateIds: this.clientTeamTemplateIds,
      vendorTeamTemplateIds: this.vendorTeamTemplateIds,
      clientPolicies: this.clientPolicies,
      vendorPolicies: this.vendorPolicies,
      conflictsCheckWaived: this.conflictsCheckWaived,
      conflicts: this.conflicts,
    };
  }
}
